import { company } from '~/company/Company';
import { DOMAIN, REASON, reportWarning } from '~/services/telemetry';
import { orderStore, userStore } from '~/stores';

/**
 * Why the transition to the checkout cannot go through.
 *
 * `noAddress` — nothing is selected to deliver to.
 * `deliveryUnavailable` — an address is selected, but no warehouse serves it,
 * so the checkout could neither price the cart nor submit an order.
 */
export type CheckoutBlocker = 'noAddress' | 'deliveryUnavailable';

/** Sentry cluster per blocker, one message each so cohorts stay separable. */
const BLOCKER_MESSAGES: Record<CheckoutBlocker, string> = {
  noAddress: 'Blocked: no delivery address',
  deliveryUnavailable: 'Blocked: delivery unavailable for address',
};

/**
 * Blockers already reported in this session.
 *
 * Module-scoped rather than per hook instance: the hook is re-created on every
 * mount of the bottom bar, the cart summary and the recommendations modal, so a
 * ref-based guard resets on each navigation. "No address" is the ordinary state
 * of a first visit and the button now invites tapping, so without this the
 * routine case would file a warning per tap — into a queue the Sentry facade
 * caps at 100 entries before the SDK arrives, dropping what overflows.
 */
const reported = new Set<CheckoutBlocker>();

/** Reports a blocked transition once per session, per reason. */
export const reportCheckoutBlocked = (
  blocker: CheckoutBlocker,
  extras: Record<string, unknown>,
): void => {
  if (reported.has(blocker)) {
    return;
  }
  reported.add(blocker);

  reportWarning(DOMAIN.checkout, BLOCKER_MESSAGES[blocker], {
    reason: REASON.checkout.blocked,
    fingerprint: [blocker],
    extras,
  });
};

/**
 * The reason the checkout is out of reach right now, or `null` when it is not.
 *
 * Both callers of this — the transition itself and the buttons that trigger it
 * — have to agree, or the UI promises something the gate then refuses. Reading
 * `etaCalculation` instead would not work: it holds the last calculation that
 * succeeded and survives the one that fails, so an address whose zone was
 * switched off still looks deliverable through it.
 */
export const checkoutBlocker = (): CheckoutBlocker | null => {
  // Both blockers are resolved on the checkout itself, which owns the choice.
  if (company.addressSelectedAtCheckout) {
    return null;
  }

  if (!userStore.deliveryAddress) {
    return 'noAddress';
  }

  if (orderStore.isDeliveryUnavailable) {
    return 'deliveryUnavailable';
  }

  return null;
};
